<script lang="ts">
	import { BaseInput, DetailedSelect, RadioGroup, SelectWithTags } from '@repo/commons/components'
	import type { DetailedSelectItem } from '@repo/commons/models'
	import {
		isListValueType,
		toDetailedSelectItem,
		toLeverValue,
		type LeverFormValue
	} from '@repo/commons/utils'
	import {
		CustomTypeEnum,
		type Lever
	} from '@semanticedge/persistence-service-noheaders-client/src'
	import { t } from 'svelte-i18n'
	import { BOOLEAN_RADIO_GROUP_NAME } from './constants.js'

	type Props = {
		lever?: Pick<Lever, 'leverType' | 'possibleValues'>
		error?: string
		label?: string
		placeholder?: string
		addValueText?: string
		numberInput?: boolean
		formValue: LeverFormValue
		onBlur?: VoidFunction
		onChange: (formValue: LeverFormValue) => void
	}

	const {
		lever,
		formValue,
		error,
		label: customLabel,
		placeholder,
		addValueText,
		numberInput = false,
		onBlur,
		onChange
	}: Props = $props()

	const leverType = $derived(lever?.leverType)
	const isList = $derived(isListValueType(leverType))
	const isBoolean = $derived(leverType === CustomTypeEnum.Boolean)
	const isCategorical = $derived((lever?.possibleValues?.length ?? 0) > 0)
	const isBooleanList = $derived(leverType === CustomTypeEnum.BooleanList)
	const label = $derived(customLabel ?? $t('LeverValueField.value'))
	const noResultsButtonText = $derived(addValueText ?? $t('LeverValueField.addValue'))
	const numberType = $derived(
		numberInput && (leverType === CustomTypeEnum.Integer || leverType === CustomTypeEnum.Float)
			? leverType
			: undefined
	)
	const inputType = $derived(numberType ? 'number' : 'text')

	const booleanOptions: DetailedSelectItem[] = $derived([
		{ id: 'true', title: $t('LeverValueField.true') },
		{ id: 'false', title: $t('LeverValueField.false') }
	])

	const booleanRadioOptions = $derived(
		booleanOptions.map(({ id, title }) => ({ value: id, label: title }))
	)

	const valueOptions: DetailedSelectItem[] = $derived(
		(lever?.possibleValues ?? []).map(toDetailedSelectItem)
	)

	const selectedValueOption = $derived(
		valueOptions.find(({ id }) => id === formValue.value) ?? null
	)

	const listItems: DetailedSelectItem[] = $derived(
		isBooleanList ? booleanOptions : formValue.values.map(toDetailedSelectItem)
	)

	const selectedListItems = $derived(
		isBooleanList
			? booleanOptions.filter(({ id }) => formValue.values.includes(String(id)))
			: listItems
	)

	const handleValueChange = (value: string): void => {
		onChange({ ...formValue, value })
	}

	const handleOptionSelect = (item: DetailedSelectItem | null): void => {
		handleValueChange(String(item?.id ?? ''))
	}

	const handleRadioChange = (selectedValue: unknown): void => {
		handleValueChange(String(selectedValue))
	}

	const handleListChange = (items: DetailedSelectItem[]): void => {
		onChange({ ...formValue, values: items.map(({ id }) => String(id)) })
	}

	const addListValue = (rawValue: string): void => {
		const value = rawValue.trim()

		if (toLeverValue(value, leverType) === null || formValue.values.includes(value)) return

		onChange({ ...formValue, values: [...formValue.values, value] })
	}
</script>

{#if isList}
	<SelectWithTags
		{label}
		{placeholder}
		disabled={!lever}
		items={listItems}
		selectedItems={selectedListItems}
		showNoResultsButton={!isBooleanList}
		{noResultsButtonText}
		onNoResultsButtonClick={addListValue}
		onMultiSelect={handleListChange}
	/>
{:else if isBoolean}
	<RadioGroup
		name={BOOLEAN_RADIO_GROUP_NAME}
		legend={label}
		{error}
		value={formValue.value}
		options={booleanRadioOptions}
		onchange={handleRadioChange}
	/>
{:else if isCategorical}
	<DetailedSelect
		{label}
		{placeholder}
		{error}
		disabled={!lever}
		items={valueOptions}
		selectedItem={selectedValueOption}
		onSelect={handleOptionSelect}
	/>
{:else}
	<BaseInput
		{label}
		{placeholder}
		{numberType}
		type={inputType}
		size="md"
		disabled={!lever}
		value={formValue.value}
		errorMessage={error}
		showError={!!error}
		{onBlur}
		onInput={handleValueChange}
	/>
{/if}
